import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { EASE, MagneticButton, TiltCard } from "./Motion";

const W = ({ children, delay, className = "" }) => (
  <span className="inline-block overflow-hidden align-bottom">
    <motion.span
      className={`inline-block will-change-transform ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const ticks = [
  { label: "Instagram · Reach Boost", value: "2.4M", status: "Delivering" },
  { label: "YouTube · Watch Hours", value: "860K", status: "Processing" },
  { label: "Telegram · Members", value: "120K", status: "Completed" },
];

export default function Hero({ start, content }) {
  const eyebrow = content?.hero_eyebrow || "Global growth engine · Est. 2018";
  const line1 = (content?.hero_line_1 || "ATTENTION IS").toUpperCase();
  const line2 = (content?.hero_line_2 || "THE NEW").toUpperCase();
  const goldWord = (content?.hero_line_gold || "GOLD.").toUpperCase();
  const subline = content?.hero_subline || "and we mint it into empires.";
  const paragraph =
    content?.hero_paragraph ||
    "BEATKINGSMM is the growth engine behind 12,000+ creators and brands — precision SMM, curated brand deals and smarter global sourcing, under one obsidian roof.";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        className="absolute -top-40 left-1/4 h-[560px] w-[560px] rounded-full bg-gold/[0.07] blur-[140px]"
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 right-0 h-[480px] w-[480px] rounded-full bg-goldbright/[0.05] blur-[130px]"
        animate={{ x: [0, -50, 0], y: [0, 40, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute right-[12%] top-[18%] hidden h-28 w-28 rounded-full border border-gold/20 bg-white/[0.03] backdrop-blur-xl lg:block"
        animate={{ y: [0, -22, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-[16%] left-[6%] hidden h-16 w-16 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl lg:block"
        animate={{ y: [0, 18, 0], rotate: [0, 8, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 pt-36 sm:px-8 lg:grid-cols-12 lg:px-16"
      >
        <div className="lg:col-span-7">
          {start && (
            <>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-gold/80"
                data-testid="hero-eyebrow"
              >
                <span className="h-px w-10 bg-gold/60" />
                {eyebrow}
              </motion.p>

              <h1
                data-testid="hero-headline"
                className="text-5xl font-bold leading-[1.02] tracking-tight text-neutral-50 sm:text-7xl lg:text-[5.4rem]"
              >
                {line1.split(" ").map((w, i) => (
                  <W key={`l1-${i}`} delay={0.25 + i * 0.08}>{w + " "}</W>
                ))}
                <br />
                {line2.split(" ").map((w, i) => (
                  <W key={`l2-${i}`} delay={0.45 + i * 0.08}>{w + " "}</W>
                ))}
                <W delay={0.45 + line2.split(" ").length * 0.08} className="text-gold-grad">
                  {goldWord}
                </W>
              </h1>

              <div className="mt-4 overflow-hidden">
                <motion.p
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, ease: EASE, delay: 0.68 }}
                  className="flex items-center gap-2.5 font-serif text-2xl italic text-neutral-300 sm:text-3xl"
                >
                  {subline}
                  <motion.span
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 1.5 }}
                    className="inline-flex"
                    data-testid="hero-verified-badge"
                  >
                    <BadgeCheck size={24} className="text-gold" fill="rgb(var(--gold) / 0.15)" />
                  </motion.span>
                </motion.p>
              </div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: EASE, delay: 0.85 }}
                className="mt-8 max-w-xl text-base font-light leading-relaxed text-neutral-400 sm:text-lg"
                data-testid="hero-subcopy"
              >
                {paragraph}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: EASE, delay: 1 }}
                className="mt-10 flex flex-wrap items-center gap-5"
              >
                <MagneticButton href="#services" testid="hero-cta-primary">
                  Start a Campaign <ArrowUpRight size={16} />
                </MagneticButton>
                <MagneticButton href="/panel/signup" variant="ghost" testid="hero-cta-signup">
                  Sign Up
                </MagneticButton>
                <MagneticButton href="/panel/login" variant="ghost" testid="hero-cta-login">
                  Log In
                </MagneticButton>
              </motion.div>
            </>
          )}
        </div>

        <div className="lg:col-span-5">
          {start && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.9 }}
            >
              <TiltCard intensity={9} testid="hero-tilt-card">
                <div className="glass relative overflow-hidden rounded-3xl p-7">
                  <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gold/80">
                      LIVE ORDER PULSE
                    </span>
                    <span className="flex items-center gap-2 text-[11px] font-medium text-emerald-400">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                      </span>
                      LIVE
                    </span>
                  </div>

                  <div className="mt-6 space-y-4">
                    {ticks.map((t, i) => (
                      <motion.div
                        key={t.label}
                        initial={{ opacity: 0, x: 24 }}
                        animate={start ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 1.1 + i * 0.15, duration: 0.8, ease: EASE }}
                        className="flex items-center justify-between border-b border-white/5 pb-4"
                      >
                        <div>
                          <div className="text-sm font-medium text-neutral-200">{t.label}</div>
                          <div className="mt-0.5 font-mono text-[10px] tracking-widest text-neutral-500">
                            {t.status.toUpperCase()}
                          </div>
                        </div>
                        <div className="font-serif text-xl italic text-goldlight">{t.value}</div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="rounded-xl border border-gold/15 bg-white/[0.02] p-4">
                      <div className="text-2xl font-bold text-neutral-50">42 Cr+</div>
                      <div className="mt-1 font-mono text-[9px] tracking-[0.25em] text-neutral-500">
                        ORDERS PLACED
                      </div>
                    </div>
                    <div className="rounded-xl border border-gold/15 bg-white/[0.02] p-4">
                      <div className="text-2xl font-bold text-neutral-50">₹710 Cr+</div>
                      <div className="mt-1 font-mono text-[9px] tracking-[0.25em] text-neutral-500">
                        CLIENT VALUE
                      </div>
                    </div>
                  </div>
                </div>
              </TiltCard>
            </motion.div>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.7, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-neutral-500">SCROLL</span>
        <div className="h-12 w-px overflow-hidden bg-white/10">
          <motion.div
            className="h-1/2 w-full bg-gold"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
